import { useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import type { EvidenceItem, IntervieweeCard, IntervieweeDetail } from "../../../domain/contracts";
import { intervieweeDetailQueryOptions } from "../../../services/queries/publicQueryOptions";
import type { VillageIncidentChartCardData } from "../model/villageIncidentCharts";
import { useVillageIncidentAggregates } from "./useVillageIncidentAggregates";

const DETAIL_BATCH_SIZE = 8;

export type VillageIncidentChartsPanelData = {
  chartCards: VillageIncidentChartCardData[];
  isLoadingDetails: boolean;
  isProgressiveLoading: boolean;
  loadedDetailCount: number;
  totalDetailCount: number;
};

export function useVillageIncidentChartsPanelData(
  interviewees: IntervieweeCard[],
  evidenceItems: EvidenceItem[],
): VillageIncidentChartsPanelData {
  const totalDetailCount = interviewees.length;
  const [enabledDetailCount, setEnabledDetailCount] = useState(() => Math.min(DETAIL_BATCH_SIZE, totalDetailCount));

  useEffect(() => {
    setEnabledDetailCount(Math.min(DETAIL_BATCH_SIZE, totalDetailCount));
  }, [totalDetailCount]);

  const detailQueries = useQueries({
    queries: interviewees.map((interviewee, index) => {
      const baseOptions = intervieweeDetailQueryOptions(interviewee.id);
      return {
        ...baseOptions,
        enabled: index < enabledDetailCount && Boolean(baseOptions.enabled),
      };
    }),
  });

  const activeWindowCount = Math.min(enabledDetailCount, totalDetailCount);

  const hasPendingInActiveWindow = useMemo(
    () => detailQueries
      .slice(0, activeWindowCount)
      .some((query) => query.isPending || query.fetchStatus === "fetching"),
    [activeWindowCount, detailQueries],
  );

  useEffect(() => {
    if (activeWindowCount >= totalDetailCount) {
      return;
    }

    if (hasPendingInActiveWindow) {
      return;
    }

    setEnabledDetailCount((current) => Math.min(current + DETAIL_BATCH_SIZE, totalDetailCount));
  }, [activeWindowCount, hasPendingInActiveWindow, totalDetailCount]);

  const intervieweeDetails = useMemo(
    () => detailQueries
      .map((query) => query.data?.interviewee)
      .filter((item): item is IntervieweeDetail => Boolean(item)),
    [detailQueries],
  );

  const loadedDetailCount = intervieweeDetails.length;
  const isLoadingDetails = detailQueries.some((query) => query.isPending || query.fetchStatus === "fetching");
  const isProgressiveLoading = totalDetailCount > 0 && loadedDetailCount < totalDetailCount;
  const { chartCards } = useVillageIncidentAggregates(intervieweeDetails, evidenceItems);

  return {
    chartCards,
    isLoadingDetails,
    isProgressiveLoading,
    loadedDetailCount,
    totalDetailCount,
  };
}
